/**
 * One account, and the three things an administrator does to it.
 *
 * * **Change what they hold** — roles, at their scopes; policies, which are the
 *   only place a deny is written.
 * * **Turn the account off** — immediate, because authority is resolved from
 *   the database on every request rather than carried in the token.
 * * **Reset the password** — typed or generated, with or without a change at
 *   the next sign-in (see `ResetPasswordDialog`, which a client's page uses
 *   too). Either way it ends every session the account had.
 *
 * Roles are saved as a **set**, in one request. The screen shows a set and
 * saves a set; a per-assignment API underneath one save button turns a single
 * visible change into several calls that can half-fail.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldAlert } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  DetailField,
  DetailGrid,
  Input,
  Modal,
  QueryPending,
  Select,
  Switch,
  api,
  formatDateTime,
  toast,
  useCurrentUser,
} from '@connect/web-shared';

import { ResetPasswordDialog } from './reset-password-dialog';
import { RoleAssignmentEditor, type Assignment } from './role-assignment-editor';

interface AssignmentRow {
  role_id: string;
  role_name: string;
  scope_kind: string;
  department_id: string | null;
  department_name: string | null;
  team_id: string | null;
  team_name: string | null;
}

interface UserDetail {
  public_id: string;
  username: string;
  email: string;
  /** Signs in too, and receives sign-in codes. Optional. */
  login_mobile: string | null;
  full_name: string;
  job_title: string | null;
  code: string | null;
  is_active: boolean;
  is_superuser: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  roles: AssignmentRow[];
  policies: { public_id: string; name: string }[];
}

interface Policy {
  public_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
}

export function UserDetailDialog({
  open,
  onClose,
  publicId,
}: {
  open: boolean;
  onClose: () => void;
  publicId: string | null;
}) {
  const queryClient = useQueryClient();
  const me = useCurrentUser();
  const [roles, setRoles] = useState<Assignment[]>([]);
  const [policies, setPolicies] = useState<string[]>([]);
  const [resetting, setResetting] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');

  const user = useQuery({
    queryKey: ['iam', 'users', publicId],
    queryFn: () => api.get<UserDetail>(`/iam/users/${publicId}`),
    enabled: open && publicId !== null,
  });

  const allPolicies = useQuery({
    queryKey: ['iam', 'policies'],
    queryFn: () => api.get<Policy[]>('/iam/policies'),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!user.data) return;
    setRoles(
      user.data.roles.map((row) => ({
        role_id: row.role_id,
        scope_kind: row.scope_kind as Assignment['scope_kind'],
        // The ids, not the names. Saving the set back with a name would lose
        // the scope of an assignment nobody touched.
        department_id: row.department_id,
        team_id: row.team_id,
      })),
    );
    setPolicies(user.data.policies.map((p) => p.public_id));
    setUsername(user.data.username);
    setEmail(user.data.email);
    setMobile(user.data.login_mobile ?? '');
  }, [user.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['iam'] });
  };

  const saveRoles = useMutation({
    mutationFn: () =>
      api.put(`/iam/users/${publicId}/roles`, {
        roles: roles.filter((r) => r.role_id),
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Roles saved');
    },
    onError: (error) => {
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined);
    },
  });

  const savePolicies = useMutation({
    mutationFn: () => api.put(`/iam/users/${publicId}/policies`, { policies }),
    onSuccess: () => {
      invalidate();
      toast.success('Policies saved');
    },
    onError: (error) => {
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined);
    },
  });

  const saveSignIn = useMutation({
    mutationFn: () =>
      api.patch(`/iam/users/${publicId}`, {
        username: username.trim(),
        email: email.trim(),
        // Empty removes it: the one sign-in name an account may be without.
        login_mobile: mobile.trim(),
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Sign-in details saved');
    },
    onError: (error) => {
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined);
    },
  });

  const setActive = useMutation({
    mutationFn: (isActive: boolean) =>
      api.patch(`/iam/users/${publicId}`, { is_active: isActive }),
    onSuccess: () => {
      invalidate();
      toast.success('Saved', 'It takes effect on their next request.');
    },
    onError: (error) => {
      // "You cannot deactivate your own account" — the refusal names the
      // reason, so it is shown as written.
      toast.error('Could not change that', error instanceof ApiError ? error.message : undefined);
    },
  });

  const row = user.data;
  const isSelf = row?.public_id === me?.public_id;
  const signInName = row?.username || row?.email || '';
  const signInChanged =
    row !== undefined &&
    (username.trim() !== row.username ||
      email.trim() !== row.email ||
      mobile.trim() !== (row.login_mobile ?? ''));
  // A sign-in code goes to an address or number the account signs in with, so
  // moving a superuser's is taking it over: only a superuser may, and the API
  // refuses anybody else.
  const mayEditSignIn = row !== undefined && (!row.is_superuser || Boolean(me?.is_superuser));

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={row?.full_name || signInName || 'Account'}
        description={row ? [row.username, row.email].filter(Boolean).join(' · ') : undefined}
        size="lg"
        footer={
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        }
      >
        {row === undefined ? (
          <QueryPending query={user} label="Loading the account" />
        ) : (
          <div className="space-y-5">
            <DetailGrid>
              <DetailField label="Username">{row.username ?? '—'}</DetailField>
              <DetailField label="Email">{row.email ?? '—'}</DetailField>
              <DetailField label="Sign-in mobile">{row.login_mobile ?? '—'}</DetailField>
              <DetailField label="Code">{row.code ?? '—'}</DetailField>
              <DetailField label="Job title">{row.job_title ?? '—'}</DetailField>
              <DetailField label="Last signed in">
                {row.last_login_at ? formatDateTime(row.last_login_at) : 'Never'}
              </DetailField>
              <DetailField label="Created">{formatDateTime(row.created_at)}</DetailField>
            </DetailGrid>

            {row.is_superuser ? (
              <Alert tone="danger" title="Superuser">
                <span className="flex items-start gap-1.5">
                  <ShieldAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    This account bypasses every permission check. It does <strong>not</strong>{' '}
                    bypass the Service Switch — a service the installation does not have is
                    unreachable even here.
                  </span>
                </span>
              </Alert>
            ) : null}

            {mayEditSignIn ? (
              <Can permission="iam.user.edit">
                <div className="space-y-2">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Input
                      label="Username"
                      value={username}
                      autoCapitalize="none"
                      spellCheck={false}
                      onChange={(event) => setUsername(event.target.value.toLowerCase())}
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                    <Input
                      label="Sign-in mobile"
                      type="tel"
                      inputMode="tel"
                      value={mobile}
                      placeholder="Optional"
                      onChange={(event) => setMobile(event.target.value)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-2xs text-faint">
                      Username and email are required; the mobile is optional. Any of them signs
                      in, with the password or a one-time code — a code goes to the mobile, or to
                      the email.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={saveSignIn.isPending}
                      disabled={!signInChanged || !username.trim() || !email.trim()}
                      onClick={() => saveSignIn.mutate()}
                    >
                      Save sign-in
                    </Button>
                  </div>
                </div>
              </Can>
            ) : null}

            <Can permission="iam.user.edit">
              <Switch
                label="Can sign in"
                checked={row.is_active}
                disabled={isSelf || setActive.isPending}
                hint={
                  isSelf
                    ? 'You cannot deactivate your own account — ask another administrator, so there is always a way back.'
                    : 'Takes effect on their next request, not when their token expires.'
                }
                onChange={(event) => setActive.mutate(event.target.checked)}
              />
            </Can>

            <Can permission="iam.user.assign">
                <div className="space-y-2">
                  <RoleAssignmentEditor value={roles} onChange={setRoles} />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      loading={saveRoles.isPending}
                      onClick={() => saveRoles.mutate()}
                    >
                      Save roles
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Select
                    label="Attach a policy"
                    options={[
                      { value: '', label: 'Choose a policy' },
                      ...(allPolicies.data ?? [])
                        .filter((p) => !policies.includes(p.public_id))
                        .map((p) => ({ value: p.public_id, label: p.name })),
                    ]}
                    value=""
                    hint="A policy is where a deny is written, and a deny beats every allow."
                    onChange={(event) => {
                      if (event.target.value) {
                        setPolicies((current) => [...current, event.target.value]);
                      }
                    }}
                  />
                  {policies.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {policies.map((id) => {
                        const policy = allPolicies.data?.find((p) => p.public_id === id);
                        return (
                          <Badge key={id} tone="neutral">
                            {policy?.name ?? id}
                            <button
                              type="button"
                              className="ml-1 text-faint hover:text-danger"
                              aria-label={`Detach ${policy?.name ?? 'policy'}`}
                              onClick={() =>
                                setPolicies((current) => current.filter((p) => p !== id))
                              }
                            >
                              ×
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={savePolicies.isPending}
                      onClick={() => savePolicies.mutate()}
                    >
                      Save policies
                    </Button>
                  </div>
                </div>
              </Can>

            <Can permission="iam.user.edit">
              <div className="flex items-center justify-between gap-3 border-t border-line pt-4">
                <p className="text-2xs text-faint">
                  A reset sets a new password — one you type, or a generated one shown once — and
                  signs out every session this account has.
                </p>
                <Button variant="secondary" size="sm" onClick={() => setResetting(true)}>
                  <KeyRound className="size-3.5" />
                  Reset password
                </Button>
              </div>
            </Can>
          </div>
        )}
      </Modal>

      <ResetPasswordDialog
        open={resetting}
        onClose={() => setResetting(false)}
        endpoint={publicId ? `/iam/users/${publicId}/reset-password` : null}
        name={row?.full_name || signInName || 'This account'}
      />
    </>
  );
}
